import React, { useState, useCallback } from 'react';

type GitHubImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onImport: (files: Record<string, string>) => void;
};

type GitHubContent = {
  name: string;
  path: string;
  type: 'file' | 'dir';
  download_url: string | null;
  content?: string;
  encoding?: string;
};

export const GitHubImportModal = React.memo(function GitHubImportModal({
  isOpen,
  onClose,
  onImport,
}: GitHubImportModalProps) {
  const [repoUrl, setRepoUrl] = useState('');
  const [branch, setBranch] = useState('main');
  const [path, setPath] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  // Parse GitHub URL or owner/repo format
  const parseRepoUrl = (input: string): { owner: string; repo: string } | null => {
    // Try URL format: https://github.com/owner/repo
    const urlMatch = input.match(/github\.com\/([^/]+)\/([^/]+)/);
    if (urlMatch) {
      return { owner: urlMatch[1], repo: urlMatch[2].replace(/\.git$/, '') };
    }
    // Try owner/repo format
    const shortMatch = input.match(/^([^/]+)\/([^/]+)$/);
    if (shortMatch) {
      return { owner: shortMatch[1], repo: shortMatch[2] };
    }
    return null;
  };

  const fetchContents = useCallback(async (
    owner: string,
    repo: string,
    contentPath: string,
    headers: HeadersInit
  ): Promise<GitHubContent[]> => {
    const apiUrl = `https://api.github.com/repos/${owner}/${repo}/contents/${contentPath}?ref=${branch}`;
    const response = await fetch(apiUrl, { headers });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Path not found: ${contentPath || '/'}`);
      }
      if (response.status === 403) {
        throw new Error('Rate limit exceeded. Please provide a GitHub token.');
      }
      throw new Error(`GitHub API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [data];
  }, [branch]);

  const fetchFileContent = useCallback(async (
    downloadUrl: string,
    headers: HeadersInit
  ): Promise<string> => {
    const response = await fetch(downloadUrl, { headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }
    return response.text();
  }, []);

  const handleImport = useCallback(async () => {
    const parsed = parseRepoUrl(repoUrl);
    if (!parsed) {
      setError('Invalid repository URL. Use https://github.com/owner/repo or owner/repo format.');
      return;
    }

    setLoading(true);
    setError(null);
    setProgress('Connecting to GitHub...');

    const headers: HeadersInit = {
      'Accept': 'application/vnd.github+json',
    };
    if (token.trim()) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const files: Record<string, string> = {};
      const stack: string[] = [path];
      let fileCount = 0;

      while (stack.length > 0) {
        const currentPath = stack.pop()!;
        setProgress(`Scanning: ${currentPath || '/'}`);

        const contents = await fetchContents(parsed.owner, parsed.repo, currentPath, headers);

        for (const item of contents) {
          if (item.type === 'dir') {
            stack.push(item.path);
          } else if (item.type === 'file' && item.download_url) {
            // Skip binary files based on extension
            const ext = item.name.split('.').pop()?.toLowerCase();
            const binaryExts = ['png', 'jpg', 'jpeg', 'gif', 'ico', 'webp', 'svg', 'woff', 'woff2', 'ttf', 'eot', 'mp3', 'mp4', 'webm', 'pdf', 'zip', 'tar', 'gz'];
            if (binaryExts.includes(ext || '')) {
              continue;
            }

            setProgress(`Fetching: ${item.path}`);
            try {
              const content = await fetchFileContent(item.download_url, headers);
              // Normalize path - remove leading path prefix if specified
              let filePath = item.path;
              if (path && filePath.startsWith(path)) {
                filePath = filePath.slice(path.length).replace(/^\//, '');
              }
              files[filePath] = content;
              fileCount++;
            } catch (err) {
              console.warn(`Skipping file ${item.path}:`, err);
            }
          }
        }
      }

      if (fileCount === 0) {
        throw new Error('No files found to import');
      }

      setProgress(`Imported ${fileCount} files`);
      onImport(files);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import from GitHub');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  }, [repoUrl, branch, path, token, fetchContents, fetchFileContent, onImport]);

  const handleClose = useCallback(() => {
    setError(null);
    setProgress(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        className="modal github-import-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '500px' }}
      >
        <div className="modal-header">
          <div className="modal-title">Import from GitHub</div>
          <button className="modal-close" onClick={handleClose}>
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '16px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
              Repository URL or owner/repo:
            </label>
            <input
              type="text"
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              placeholder="https://github.com/owner/repo or owner/repo"
              style={{
                width: '100%',
                padding: '8px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                color: 'var(--text)',
                fontSize: '13px',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                Branch:
              </label>
              <input
                type="text"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                placeholder="main"
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  color: 'var(--text)',
                  fontSize: '13px',
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                Path (optional):
              </label>
              <input
                type="text"
                value={path}
                onChange={(e) => setPath(e.target.value)}
                placeholder="src"
                style={{
                  width: '100%',
                  padding: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  color: 'var(--text)',
                  fontSize: '13px',
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
              GitHub Token (optional, for private repos):
            </label>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="ghp_xxxxxxxxxxxx"
              style={{
                width: '100%',
                padding: '8px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                color: 'var(--text)',
                fontSize: '13px',
              }}
            />
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
              Required for private repos. <a href="https://github.com/settings/tokens/new?scopes=repo" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>Create token</a>
            </div>
          </div>

          {progress && (
            <div style={{
              padding: '12px',
              background: 'var(--bg-secondary)',
              borderRadius: '4px',
              marginBottom: '12px',
              fontSize: '12px',
              color: 'var(--muted)'
            }}>
              {progress}
            </div>
          )}

          {error && (
            <div style={{ color: 'var(--error)', fontSize: '12px', marginBottom: '12px' }}>
              {error}
            </div>
          )}

          <div style={{
            padding: '12px',
            background: 'var(--bg-secondary)',
            borderRadius: '4px',
            fontSize: '11px',
            color: 'var(--muted)'
          }}>
            <strong>Note:</strong> Binary files (images, fonts, etc.) will be skipped.
            For large repositories, consider specifying a path to import only a subdirectory.
          </div>
        </div>

        <div className="modal-footer">
          <button className="pill-btn" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="pill-btn"
            onClick={handleImport}
            disabled={loading || !repoUrl.trim()}
            style={{ opacity: loading || !repoUrl.trim() ? 0.5 : 1 }}
          >
            {loading ? 'Importing...' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default GitHubImportModal;
